import { ReactNode } from 'react';
import {
  ModuleTopBar,
  Box,
  Flex,
  Grid,
  Text,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import AnvaFlowProductsBottomBar from '@flows/src/components/flow-products/AnvaFlowProductsBottomBar';
import AnvaFlowProductsSidebar from '@flows/src/components/flow-products/AnvaFlowProductsSidebar';
import { AnvaBasketCalculationProvider } from '@flows/src/components/flow-products/quotes/basket/AnvaBasketCalculationProvider';
import { useHistory, useParams } from 'react-router';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import useStartAnvaFlow from '@sales-conversation/src/hooks/anva/useStartAnvaFlow';
import { useQueryClient } from 'react-query';

const AnvaFlowProductsLayout = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || undefined;
  const { data: session } = useFlowsSession(sessionId);
  const startAnvaFlow = useStartAnvaFlow();
  const history = useHistory();
  const { flowId } = useParams<{ flowId: string }>();
  const queryClient = useQueryClient();

  const getBackText = () => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('BACK-TO-DASHBOARD');
    }
    if (window.location.pathname.includes('basket')) {
      return t('BACK-TO-OVERVIEW');
    }
    return t('Back to questions');
  };
  const handlePrevious = () => {
    if (window.location.pathname.includes('basket')) {
      // OK way to preserve url state params
      return history.goBack();
    }
    if (flowId && sessionId) {
      // Clear the flow-products cache when navigating back to questionnaire
      // This ensures that when the user returns to the flow-products overview,
      // new quotes are calculated and the redirect logic works properly
      queryClient.removeQueries(['flow-products', 'anva', 'quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'completed-quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'quotes-v2']);

      startAnvaFlow({
        flowId: flowId,
        uuid: sessionId,
        partyId: session?.prospect?.id,
        anva_products: session?.anva_products || [],
      });
    }
  };

  const getTopBarText = (): string => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_TOP-BAR_TITLE');
    }
    return t('CONVERSATIONS_REPORT-CREATION_PRODUCT-SELECTION');
  };

  const isBasketPage = (() => {
    const path = window.location.pathname;
    return path.includes('basket') || path.includes('selection-overview');
  })();

  // The core layout content
  const layoutContent = (
    <Grid
      templateColumns={{
        base: '1fr', // Mobile: single column
        tablet: 'auto minmax(0, 1fr)', // Tablet+: sidebar + main content
      }}
      w="100%"
      h="100%"
      position="relative"
      minH="0"
    >
      {/* Sidebar - hidden on mobile */}
      <Box display={{ base: 'none', tablet: 'block' }}>
        <AnvaFlowProductsSidebar />
      </Box>

      <Grid templateRows="auto 1fr auto" position="relative" w="100%" minW="0">
        {/* Mobile Header */}
        <Box
          display={{ base: 'block', tablet: 'none' }}
          position="sticky"
          top="0"
          bg="white"
          zIndex="2"
          borderBottom="1px"
          borderColor="gray.50"
        >
          <VStack spacing="0" align="stretch">
            {/* Title */}
            <Flex align="center" justify="center" px="4" py="3" h="60px">
              <Text
                textStyle="h6Headline"
                textAlign="center"
                fontSize="md"
                fontWeight="600"
                color="gray.800"
                noOfLines={1}
              >
                {getTopBarText()}
              </Text>
            </Flex>
          </VStack>
        </Box>

        {/* Desktop Header */}
        <Box
          display={{ base: 'none', tablet: 'block' }}
          position="sticky"
          top="0"
          bg="white"
          zIndex="1"
        >
          <ModuleTopBar title={getTopBarText()} />
        </Box>

        <Flex
          flexDirection="column"
          align="center"
          flexGrow="1"
          bg="gray.ultraLight"
          overflow="auto"
          minW="0"
        >
          {children}
        </Flex>

        <AnvaFlowProductsBottomBar
          handlePrevious={handlePrevious}
          backText={getBackText()}
        />
      </Grid>
    </Grid>
  );

  // Wrap with appropriate BasketCalculationProvider when on basket page so that calculation
  // state is shared between the page content and the bottom bar actions.
  if (isBasketPage) {
    return (
      <AnvaBasketCalculationProvider sessionId={sessionId || ''}>
        {layoutContent}
      </AnvaBasketCalculationProvider>
    );
  }

  return layoutContent;
};

export default AnvaFlowProductsLayout;
